/**
 * JP Ads Banner Control Utilities
 *
 * @context
 * https://healingpaper.slack.com/archives/C027T02M1L6/p1760682709289289
 *
 * @desc
 * JP 환경에서 특정 광고(utm_source) 유입된 비로그인 유저에게
 * 앱 다운로드 배너들을 숨기기 위한 유틸리티 함수들
 *
 * @apply-to
 * 1. useAppDownloadModal - 홈 다운로드 팝업
 * 2. SignUpPopup - 회원가입 완료 포인트 팝업
 * 3. TopDownloadScreen - 상단 다운로드 배너
 *
 * @utm-parameter
 * 현재: line_ads (대소문자 무관)
 * 확장 가능: TARGET_UTM_SOURCES 배열에 추가
 *
 * @example
 * https://www.gangnamunni.com/jp?utm_source=line_ads&utm_campaign=...
 *
 * @behavior
 * - JP 환경만 적용
 * - line_ads utm_source 유입 시 localStorage에 영구 저장
 * - 비로그인 유저: 모든 앱 다운로드 관련 배너 숨김
 * - 로그인 유저: 배너 표시 (정상 노출)
 *
 * @todo 리팩토링 계획
 * - src/shared/utils/utm-control.ts 로 통합
 * - Paywall과 Banner의 UTM 로직 공통화
 * - localStorage 키 관리 통일
 * - 서버사이드에서 처리 가능한 부분 검토 (SSR에서 utm 파라미터 파싱)
 * - 회원가입/로그인 완료 시 utm 플래그 제거 로직 추가
 */

/**
 * localStorage 키
 */
const JP_TARGET_UTM_USER = 'jp_target_utm_user'

/**
 * 배너 숨김 대상 UTM 소스 목록
 * 필요시 배열에 추가하여 확장 가능 (예: 'google_ads', 'facebook_ads')
 */
const TARGET_UTM_SOURCES = ['line_ads'] as const

/**
 * 현재 URL에 타겟 utm_source가 있는지 확인
 *
 * @returns 타겟 utm_source가 있으면 true
 */
const hasTargetUtmSource = (): boolean => {
  if (typeof window === 'undefined') return false

  try {
    const url = new URL(window.location.href)
    const utmSource = url.searchParams.get('utm_source')

    if (!utmSource) return false

    // 대소문자 무관하게 체크
    const normalizedUtmSource = utmSource.toLowerCase()
    return TARGET_UTM_SOURCES.some((target) => normalizedUtmSource === target)
  } catch (error) {
    console.warn('Failed to check target UTM source:', error)
    return false
  }
}

/**
 * 타겟 광고 UTM 유저를 감지하고 localStorage에 저장
 *
 * @desc
 * SigninPaywall과 독립적인 localStorage 키('jp_target_utm_user')를 사용하여
 * line_ads 등 특정 utm_source만 정확히 감지 및 저장
 */
export const checkAndMarkUtmUser = (): void => {
  if (!hasTargetUtmSource()) return

  try {
    localStorage.setItem(JP_TARGET_UTM_USER, Date.now().toString())
  } catch (error) {
    console.warn('Failed to mark JP target UTM user:', error)
  }
}

/**
 * localStorage에 타겟 UTM 플래그가 있는지 확인
 *
 * @returns 과거에 타겟 utm으로 유입된 적이 있으면 true
 */
const isStoredTargetUtmUser = (): boolean => {
  if (typeof window === 'undefined') return false

  try {
    return !!localStorage.getItem(JP_TARGET_UTM_USER)
  } catch (error) {
    console.warn('Failed to check stored target UTM user:', error)
    return false
  }
}

/**
 * 현재 URL 또는 localStorage에 타겟 UTM이 있는지 확인
 *
 * @returns 타겟 UTM 유저면 true
 */
export const isTargetUtmUser = (): boolean => {
  // 현재 URL에 utm 있는지 + 과거에 utm으로 왔었는지 체크
  const hasUtmNow = hasTargetUtmSource() // URL 직접 체크 (첫 방문 대응)
  const wasUtmUser = isStoredTargetUtmUser() // localStorage 체크 (재방문 대응)

  return hasUtmNow || wasUtmUser
}

/**
 * JP 환경에서 앱 다운로드 배너를 표시할지 판단
 *
 * @param {boolean} isLoggedIn - 사용자 로그인 상태
 * @param {boolean} isJP - JP 환경 여부
 * @returns {boolean} 배너를 보여야 하면 true, 숨겨야 하면 false
 *
 * @desc
 * - JP 환경이 아니면 → true (배너 표시)
 * - 로그인 유저면 → true (배너 표시)
 * - JP + 비로그인 + 타겟 utm 유저면 → false (배너 숨김)
 *
 * @note
 * 첫 방문 시에도 즉시 동작하도록 URL 직접 체크 + localStorage 체크 병행
 */
export const showAppDownloadBannerInJP = (isLoggedIn: boolean, isJP: boolean): boolean => {
  // JP 환경이 아니면 배너 표시
  if (!isJP) return true

  // 로그인한 유저는 배너 표시
  if (isLoggedIn) return true

  // JP + 비로그인 + 타겟 utm 유저는 배너 숨김
  return !isTargetUtmUser()
}
